import { createApiMethod, createApiModule } from '../apiFactory';
import type {
  InstallParams,
  InstallResponse,
  LoginParams,
  LoginResponse,
  BuserResponse,
  userInfoResponse,
  UpdateBuserInfoParams,
  UpdateBuserInfoResponse,
  UpdateInitGuidedStepParams,
  UpdateInitGuidedStepResponse
} from './types';

/**
 * 安装Shopify商店
 * @param params 安装参数
 * @returns 安装结果
 */
export const install = async (params: InstallParams): Promise<InstallResponse> => {
  const installMethod = createApiMethod<InstallParams, InstallResponse>(
    '/api/brand/v1/installShopifyShop',
    'POST',
    'Error installing Shopify shop'
  );
  return installMethod(params);
}

/**
 * 用户登录
 * @param params 登录参数，包含shopDomain
 * @returns 登录结果
 */
export const buserAuth = async (params: LoginParams): Promise<LoginResponse> => {
  const loginMethod = createApiMethod<LoginParams, LoginResponse>(
    '/api/brand/v1/system/buserAuth',
    'POST',
    'Login failed'
  );
  
  try {
    // 调用登录API
    const response = await loginMethod(params);
    return response;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * 获取当前B端用户详情
 * @returns 返回B端用户的详细信息
 */
export const getCurrentBuser = async (): Promise<BuserResponse> => {
  const buserMethod = createApiMethod<Record<string, never>, BuserResponse>(
    '/api/brand/v1/buser/getCurrentBuser',
    'GET',
    'Failed to get current buser details'
  );
  
  try {
    // 调用获取B端用户详情API
    const response = await buserMethod({});
    return response;
  } catch (error) {
    console.error('Get current buser error:', error);
    throw error;
  }
};

/**
 * 获取用户信息
 * @returns 用户信息
 */
export const userInfo = async (): Promise<userInfoResponse> => {
  const userInfoMethod = createApiMethod<userInfoResponse>(
    '/api/brand/v1/system/userInfo',
    'POST',
    'User info failed'
  );
  const response = await userInfoMethod();
  return response as userInfoResponse;
}

/**
 * 认证相关API
 */
export const authApi = createApiModule('/api/auth', {
  fetchAuth: { // 获取认证信息
    method: 'POST',
    urlSuffix: '/auth',
    errorMessage: 'Error fetching auth settings'
  },
  login: { // 登录
    method: 'POST',
    urlSuffix: '/login',
    errorMessage: 'Login failed'
  },
  logout: { // 登出
    method: 'POST',
    urlSuffix: '/logout',
    errorMessage: 'Logout failed'
  },
  refreshToken: { // 刷新Token
    method: 'POST',
    urlSuffix: '/refresh-token',
    errorMessage: 'Refresh token failed'
  }
});

// 为了保持向后兼容，保留原有的函数名
export const fetchAuth = authApi.fetchAuth;

/**
 * 更新当前B端用户信息
 * @param params 更新参数，包含workLang等字段
 * @returns 返回更新操作结果
 */
export const updateCurrentBuserInfo = async (params: UpdateBuserInfoParams): Promise<UpdateBuserInfoResponse> => {
  const updateMethod = createApiMethod<UpdateBuserInfoParams, UpdateBuserInfoResponse>(
    '/api/brand/v1/buser/updateCurrentBuserInfo',
    'POST',
    'Failed to update current buser info'
  );
  
  try {
    // 调用更新B端用户信息API
    const response = await updateMethod(params);
    return response;
  } catch (error) {
    console.error('Update current buser info error:', error);
    throw error;
  }
};

/**
 * 步骤Key常量
 */
export const StepKeys = {
  ENABLE_APP_EMBED: 'stepKeyEnableAppEmbed',
  SET_STYLE_WIDGET: 'stepKeySetStyleWidget',
  CUSTOMIZE_HELP_CENTER: 'stepKeyCustomizeHelpCenter'
};

/**
 * 步骤状态常量
 */
export const StepStatus = {
  NOT_STARTED: 0,
  PARTIAL: 1,
  COMPLETED: 2,
  SKIPPED: 3
};

/**
 * 更新初始化引导步骤
 * @param params 更新参数，包含dismiss和stepUpdates
 * @returns 返回更新操作结果
 */
export const updateInitGuidedStep = async (params: UpdateInitGuidedStepParams): Promise<UpdateInitGuidedStepResponse> => {
  const updateMethod = createApiMethod<UpdateInitGuidedStepParams, UpdateInitGuidedStepResponse>(
    '/api/brand/v1/updateInitGuidedStep',
    'POST',
    'Failed to update initialization guided steps'
  );
  
  try {
    // 调用更新初始化引导步骤API
    const response = await updateMethod(params);
    return response;
  } catch (error) {
    console.error('Update init guided step error:', error);
    throw error;
  }
};
